"use client";

import React, { use, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import HiddenField from "./HiddenField";
import SelectField from "./SelectField";
import TextField from "./TextField";
import FilesField from "./FilesField";
import Text from "./Text";
import CaptchaField from "./CaptchaField";
import FormsDivider from "./FormsDivider";
import CheckboxField from "./CheckboxField";
import TextareaField from "./TextareaField";
import EmailField from "./EmailField";
import RadioField from "./RadioField";
import PlacesAutocompleteField from "./PlacesAutocompleteField";
import SignatureField from "./SignatureField";
import LabelField from "./LabelField";
import { validateFormData } from "@/helpers/formValidator";
import formValidationTemplate from "@/helpers/formValidatorTemplateGenerator";
import formDataTemplateGenerator from "@/helpers/formDataTemplateGenerator";
import RatingTable from "./RatingTable";
import { checkCondition } from "@/helpers/formConditionHelper";
import { generateHmac } from "@/helpers/cryptoUtils"; // Adjust path as needed

const Form = ({
  emailRecipients,
  emailNotificationFromEmail,
  emailNotificationTemplateId,
  autoResponderEmailSourceFieldName,
  autoResponderSubject,
  autoResponderContent,
  autoResponderFromEmail,
  formSubmittedAction,
  formSubmittedActionTextOrURL,
  formName,
  id,
  formId,
  index,
  inputs,
  separator,
  submitButtonId,
  submitButtonValue,
  title,
  topText,
  formClosed,
  formClosedText,
  formClosedDate,
  isPrivate,
  createCCTicket,
}) => {
  const router = useRouter();
  const [displayConditionInited, setDisplayConditionInited] = useState(false);
  const macroReplace = (text, data) => {
    const urlParams = new URLSearchParams(
      typeof window !== "undefined" ? window.location.search : ""
    );
    let replacedText = text;
    replacedText = replacedText.replace(/\{\?\s*(\w+)\s*\?\}/g, (match, p1) => {
      return urlParams.get(p1) || "";
    });
    if (!!data) {
      replacedText = replacedText.replace(
        /\{\%\s*(\w+)\s*\%\}/g,
        (match, p1) => {
          return data[p1] || "";
        }
      );
    }
    return replacedText;
  };
  //update the form inputs values with the URL parameters
  const [upDatedInputs, setUpdatedInputs] = useState(
    inputs?.map((input) =>
      !!input.value
        ? {
            ...input,
            value: macroReplace(input.value, null),
          }
        : input
    )
  );
  const captchaFieldExits =
    upDatedInputs?.find(
      (input) => input.__component === "forms.captcha-field"
    ) !== undefined;

  const [inputsValidationData, setInputsValidationData] = useState(
    formValidationTemplate(upDatedInputs)
  );

  const [inputsData, setInputsData] = useState(
    formName !== "find-a-teacher"
      ? { formName: formName, ...formDataTemplateGenerator(upDatedInputs) }
      : formDataTemplateGenerator(upDatedInputs)
  );
  const [loading, setLoading] = useState(false);
  const [formSent, setFormSent] = useState(false);
  const [filesData, setFilesData] = useState([]);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const steps = [];
  upDatedInputs?.map((input, index) => {
    if (input.__component === "forms.step") {
      steps.push({
        ...input,
        stepIndex: index,
      });
    }
  });
  const hasStep = steps.length > 0;
  if (steps.length === 0) {
    steps.push({ title: "Default", stepIndex: 0 });
  }
  const [activeStep, setActiveStep] = useState(0);

  const getFormFieldCount = async () => {
    const getFormValueCount = async (formId, fieldName, value) => {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_STAPI_USE_CLIENT_URL || process.env.NEXT_PUBLIC_STAPI_URL}/api/forms/field-count`,
        {
          method: "POST",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            formId,
            fieldName,
            value,
          }),
        }
      );
      const response = await res.json();
      if (!res.ok) {
        console.error("Error fetching field count:", response);
        return 0; // Fallback to 0 if there's an error
      } else {
        return response.count;
      }
    };

    for (const ic of upDatedInputs) {
      if (ic.display_condition !== null && ic.display_condition !== undefined) {
        const regex = /count\(\s*([^,]+?)\s*,\s*(?:"(.+?)"|(\d+))\s*\)/g;

        const matches = [...ic.display_condition.matchAll(regex)];
        for (const match of matches) {
          const expression = match[0];
          const field = match[1].trim();
          const value = match[2] !== undefined ? match[2] : match[3];

          const countValue = await getFormValueCount(id, field, value);
          ic.display_condition = ic.display_condition.replace(
            expression,
            countValue
          );
        }
      }
    }
    setDisplayConditionInited(true);
  };

  const onLoad = (autocompleteObj) => {
    setAutocomplete(autocompleteObj);
  };

  const onPlaceChanged = (e) => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      setSelectedPlace(place);
    }
  };

  const handleRecaptchaChange = (value, name) => {
    setRecaptchaValue(value);
    setInputsData({
      ...inputsData,
      [name]: value,
    });

    setInputsValidationData((prevInputsData) => ({
      ...prevInputsData,
      [name]: {
        ...prevInputsData[name],
        value: value,
        type: "recaptcha",
      },
    }));
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    var file = files[0];

    if (!file) return;
    // Rename the file with field name
    var newFile = new File([file], name + "_" + file.name, {
      type: file.type,
    });
    setInputsData({
      ...inputsData,
      [name]: newFile.name,
    });
    setFilesData([...filesData, newFile]);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked, value } = event.target;
    setInputsData({
      ...inputsData,
      [name]: checked
        ? [...(inputsData[name] || []), value] // Add the value if checked
        : (inputsData[name] || []).filter((item) => item !== value), // Remove the value if unchecked
    });

    setInputsValidationData((prevInputsData) => ({
      ...prevInputsData,
      [name]: {
        ...prevInputsData[name],
        value: checked
          ? [...(prevInputsData[name]?.value || []), value]
          : (prevInputsData[name]?.value || []).filter(
              (item) => item !== value
            ),
      },
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setInputsData({
      ...inputsData,
      [name]: value,
    });
    setInputsValidationData((prevInputsData) => ({
      ...prevInputsData,
      [name]: {
        ...prevInputsData[name],
        value: value,
        valid: true,
      },
    }));
  };

  const validateFormStepData = (step) => {
    const stepInputs = {};
    const stepStartIndex = steps[step].stepIndex;
    const stepEndIndex =
      steps[step + 1] !== undefined
        ? steps[step + 1].stepIndex
        : upDatedInputs.length;
    upDatedInputs?.map((input, index) => {
      if (index >= stepStartIndex && index < stepEndIndex) {
        if (!!input.name && !!inputsValidationData[input.name]) {
          stepInputs[input.name] = inputsValidationData[input.name];
        }
      }
    });
    //validating form inputs
    const validatedData = validateFormData(stepInputs);

    console.log("validatedData1 !!! ", validatedData);

    setInputsValidationData({ ...inputsValidationData, ...validatedData });

    if (
      !Object.keys(validatedData).every((key) => {
        const item = validatedData[key];
        if (item.valid) return true;
        else {
          const display_condition = inputs.find(
            (input) => input.name === key
          )?.display_condition;
          const isDisplayed = checkCondition(display_condition, inputsData);
          if (isDisplayed) return false;
          else return true;
        }
      })
    ) {
      return false;
    } else {
      return true;
    }
  };

  const submitHandler = async (event) => {
    console.log("Form submitHandler called !!! ");

    event.preventDefault();

    //validating form inputs
    const validatedData = validateFormData(inputsValidationData);

    console.log("validatedData2 !!! ", validatedData);

    setInputsValidationData(validatedData);

    if (
      !Object.keys(validatedData).every((key) => {
        const item = validatedData[key];
        if (item.valid) return true;
        else {
          const display_condition = inputs.find(
            (input) => input.name === key
          )?.display_condition;
          const isDisplayed = checkCondition(display_condition, inputsData);
          if (isDisplayed) return false;
          else return true;
        }
      })
    ) {
      return;
    }

    if (formName === "find-a-teacher") {
      const otherData = inputsData
        ? Object.keys(inputsData)
            .map(
              (key) =>
                encodeURIComponent(key) +
                "=" +
                encodeURIComponent(inputsData[key])
            )
            .join("&")
        : null;

      if ("place_id" in selectedPlace) {
        router.push(
          `/find-a-teacher/place?place=${selectedPlace.place_id}` +
            (otherData ? `&${otherData}` : "")
        ); //moving forward to next route
      }
      return;
    }

    const recaptchaResponse = recaptchaValue || null;
    if (captchaFieldExits && !recaptchaResponse) {
      alert('Please, check the option "I\'m not a robot" before continue 2.');
      return;
    }

    delete inputsData.captcha;
    const formData = new FormData();
    const inputsDataAugmented = {
      ...inputsData,
      emailRecipients: emailRecipients,
      emailNotificationFromEmail: emailNotificationFromEmail,
      emailNotificationTemplateId: emailNotificationTemplateId,
      autoResponderEmailSourceFieldName: autoResponderEmailSourceFieldName,
      autoResponderFromEmail: autoResponderFromEmail,
      autoResponderSubject: autoResponderSubject,
      autoResponderContent: autoResponderContent,
      isPrivate: isPrivate,
      createCCTicket: createCCTicket,
    };

    setLoading(true);

    formData.append(
      "data",
      JSON.stringify({
        payload: inputsDataAugmented,
        form: id,
        captcha: captchaFieldExits ? true : false,
        captchaResponse: recaptchaResponse,
      })
    );

    for (let i = 0; i < filesData.length; i++) {
      formData.append("files.attachments", filesData[i]);
    }

    if (formName === "ReportAnIssue") {
      const firstNameLastName = inputsDataAugmented?.FullName?.split(" ");
      const dataForCCTicket = JSON.stringify({
        reason: 54,
        firstName: firstNameLastName[0] || "",
        lastName: firstNameLastName[1] || "",
        email: inputsDataAugmented?.Email || "",
        message:
          `${inputsDataAugmented.NatureOfTheIssue} issue: ${inputsDataAugmented?.DescriptionOfIssue}` ||
          "",
      });

      const generatedHash = await generateHmac(
        dataForCCTicket,
        `${process.env.NEXT_PUBLIC_CC_TICKET_SIGNING_KEY}`
      );

      await fetch(
        `${
          process.env.NEXT_PUBLIC_DRUPAL_ADMINISTRATION_HOST
        }/contact-centre/contactus/create?_format=json&t=${Date.now()}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Exams-Secret": generatedHash,
          },
          body: dataForCCTicket,
        }
      )
        .then((response) => response.text())
        .catch((error) => console.error(error));
    }

    fetch(
      `${process.env.NEXT_PUBLIC_STAPI_USE_CLIENT_URL || process.env.NEXT_PUBLIC_STAPI_URL}/api/sent-forms`,
      {
        method: "POST",
        body: formData,
      }
    )
      .then((response) => response.text())
      .then((result) => {
        if (result.includes("id") || isPrivate) {
          setFormSent(true);
          if (formSubmittedAction === "Redirect to URL") {
            router.push(macroReplace(formSubmittedActionTextOrURL, inputsData));
          }
        } else {
          setLoading(false);
          alert("Form not sent!");
        }
      })
      .catch((error) => console.error(error));
  };

  const getRatingTableQuestions = (startIndex) => {
    let endIndex = startIndex;
    for (let i = startIndex; i < upDatedInputs.length; i++) {
      if (
        i + 1 >= upDatedInputs.length ||
        !(
          upDatedInputs[i + 1].__component === "forms.radio-field" &&
          upDatedInputs[i + 1].inRatingTable === true
        )
      ) {
        endIndex = i;
        break;
      }
    }
    const questions = upDatedInputs.slice(startIndex, endIndex + 1);
    return questions;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window?.location.search);
    if (!displayConditionInited) {
      getFormFieldCount();
    }

    //update label field value
    /*let data = inputsData;
    inputs?.map((input) => {
      if (input.__component === "forms.label") {
        data = {
          ...data,
          [input.name]: urlParams.get(input.URLParameterName),
        };
      }
    });
    setInputsData(data);*/
  }, []);

  return (
    <div className="tps-layout">
      {title &&
        (separator ? (
          <legend className={`separator-left h1`}>{title}</legend>
        ) : (
          <div role="heading" aria-level="2" className="form-title h2">
            {title}
          </div>
        ))}

      {formClosed === true ||
      (!!formClosedDate && new Date() > new Date(formClosedDate)) ? (
        <p
          dangerouslySetInnerHTML={{
            __html: formClosedText || "<h3>This form is now closed.</h3>",
          }}
        ></p>
      ) : (
        <>
          {topText && <p dangerouslySetInnerHTML={{ __html: topText }}></p>}
          <form
            key={index}
            id={formId}
            name={formName}
            data-form-id={formId}
            className="form-container form-button-alignment-left"
            onSubmit={submitHandler}
            noValidate={true}
          >
            <div className="fatv2-masthead-form-inner">
              <div
                className="fatv2-masthead-form fatv2-masthead-fake-form"
                data-algo="geo"
                data-algo-geofield="#postal-code"
                data-algo-georadiusfield="#distance"
                data-algo-index="find-a-teacher-v4"
                id="findateacher-fatlp-form"
              >
                <fieldset className="fatv2-masthead-form-fieldset">
                  <div
                    className="form-container-inner"
                    style={{ display: "block" }}
                    data-tps-step-index="0"
                  >
                    <div className={`tps-form-container active`}>
                      {hasStep && (
                        <div className="form-container-step-controls grid-x grid-padding-x align-center align-top">
                          {steps.map((step, index) => (
                            <div
                              className={`tps-form-step-control cell shrink ${
                                activeStep === index && "active"
                              }`}
                              data-target-step={index}
                              key={`step-${index}`}
                            >
                              <span className="tps-form-step-icon">
                                {index + 1}
                              </span>
                              <span className="tps-form-step-control-label show-for-tablet">
                                {step.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {steps.map((step, i) => (
                        <div
                          className={`tps-form-step ${
                            activeStep === i && "active"
                          }`}
                          key={`step-content-${i}`}
                        >
                          <div className="tps-form-question">
                            {/* ic - input components */}
                            {displayConditionInited &&
                              upDatedInputs?.map((ic, index) => {
                                //check if the input is in the current step
                                if (
                                  index >= step.stepIndex &&
                                  (steps[i + 1] === undefined ||
                                    index < steps[i + 1]?.stepIndex)
                                ) {
                                  if (
                                    ic.display_condition === null ||
                                    ic.display_condition === undefined ||
                                    (!!ic.display_condition &&
                                      checkCondition(
                                        ic.display_condition,
                                        inputsData
                                      ))
                                  ) {
                                    switch (ic.__component) {
                                      case "forms.rating-table":
                                        return (
                                          <RatingTable
                                            key={index}
                                            label={ic?.label}
                                            tableHeaders={ic?.tableHeaders}
                                            questions={getRatingTableQuestions(
                                              index + 1
                                            )}
                                            onChangeHandler={handleInputChange}
                                            inputsData={inputsData}
                                            inputsValidationData={
                                              inputsValidationData
                                            }
                                          />
                                        );
                                        break;
                                      case "page.text":
                                        return (
                                          <Text
                                            key={index}
                                            text={ic?.text}
                                            withinContainer={
                                              ic?.withinContainer
                                            }
                                            displayAsInlineBlock={
                                              ic?.displayAsInlineBlock
                                            }
                                          />
                                        );
                                        break;
                                      case "forms.hidden-field":
                                        return (
                                          <HiddenField
                                            key={index}
                                            name={ic?.name}
                                            value={ic?.value}
                                            idField={ic?.idField}
                                          />
                                        );
                                        break;
                                      case "forms.choice-field-item":
                                        return (
                                          <SelectField
                                            items={ic?.items}
                                            externalDataSource={
                                              ic?.externalData
                                            }
                                            key={index}
                                            label={ic?.label}
                                            name={ic?.name}
                                            idField={ic?.idField}
                                            required={ic?.required}
                                            onChangeHandler={handleInputChange}
                                            description={ic?.description}
                                            explanation={ic?.explanation}
                                            isValid={
                                              inputsValidationData[ic?.name]
                                                ?.valid
                                            }
                                            errorMessage={
                                              inputsValidationData[ic?.name]
                                                ?.message
                                            }
                                            inputsData={inputsData}
                                          />
                                        );
                                        break;
                                      case "forms.radio-field":
                                        return (
                                          !ic.inRatingTable === true && (
                                            <RadioField
                                              items={ic?.items}
                                              key={index}
                                              label={ic?.label}
                                              name={ic?.name}
                                              required={ic?.required}
                                              onChangeHandler={
                                                handleInputChange
                                              }
                                              value={inputsData[ic?.name] || ""}
                                              isValid={
                                                inputsValidationData[ic?.name]
                                                  ?.valid
                                              }
                                              errorMessage={
                                                inputsValidationData[ic?.name]
                                                  ?.message
                                              }
                                              description={ic?.description}
                                              explanation={ic?.explanation}
                                            />
                                          )
                                        );
                                        break;
                                      case "forms.form-item":
                                        return (
                                          <TextField
                                            idField={ic?.idField}
                                            isReadonly={ic?.isReadonly}
                                            key={index}
                                            label={ic?.label}
                                            name={ic?.name}
                                            placeholder={ic?.placeholder}
                                            required={ic?.required}
                                            type={ic?.type}
                                            value={inputsData[ic?.name] || ""}
                                            validation={ic?.validation}
                                            onChangeHandler={handleInputChange}
                                            isValid={
                                              inputsValidationData[ic?.name]
                                                ?.valid
                                            }
                                            errorMessage={
                                              inputsValidationData[ic?.name]
                                                ?.message
                                            }
                                            description={ic?.description}
                                            explanation={ic?.explanation}
                                          />
                                        );
                                        break;
                                      case "forms.label":
                                        return (
                                          <LabelField
                                            idField={ic?.idField}
                                            key={index}
                                            label={ic?.label}
                                            name={ic?.name}
                                            required={ic?.required}
                                            hidden={ic?.hidden}
                                            value={ic?.value}
                                          />
                                        );
                                        break;
                                      case "forms.textarea-field":
                                        return (
                                          <TextareaField
                                            idField={ic?.idField}
                                            key={index}
                                            label={ic?.label}
                                            name={ic?.name}
                                            cols={ic?.cols}
                                            rows={ic?.rows}
                                            placeholder={ic?.placeholder}
                                            required={ic?.required}
                                            onChangeHandler={handleInputChange}
                                            minLength={ic?.minLength}
                                            maxLength={ic?.maxLength}
                                            readOnly={ic?.readOnly}
                                            isValid={
                                              inputsValidationData[ic?.name]
                                                ?.valid
                                            }
                                            errorMessage={
                                              inputsValidationData[ic?.name]
                                                ?.message
                                            }
                                            description={ic?.description}
                                            explanation={ic?.explanation}
                                          />
                                        );
                                        break;
                                      case "forms.checkbox":
                                        return (
                                          <CheckboxField
                                            key={index}
                                            items={ic?.items}
                                            label={ic?.label}
                                            name={ic?.name}
                                            value={inputsData[ic?.name] || []}
                                            required={ic?.required}
                                            onChangeHandler={
                                              handleCheckboxChange
                                            }
                                            isValid={
                                              inputsValidationData[ic?.name]
                                                ?.valid
                                            }
                                            errorMessage={
                                              inputsValidationData[ic?.name]
                                                ?.message
                                            }
                                            description={ic?.description}
                                            explanation={ic?.explanation}
                                          />
                                        );
                                        break;
                                      case "forms.captcha-field":
                                        return (
                                          <CaptchaField
                                            key={index}
                                            name={ic?.name}
                                            handleRecaptchaChange={
                                              handleRecaptchaChange
                                            }
                                            required={true}
                                            isValid={
                                              inputsValidationData[ic?.name]
                                                ?.valid
                                            }
                                            errorMessage={
                                              inputsValidationData[ic?.name]
                                                ?.message
                                            }
                                          />
                                        );
                                        break;
                                      case "forms.forms-divider":
                                        return (
                                          <FormsDivider
                                            key={index}
                                            title={ic?.title}
                                          />
                                        );
                                        break;
                                      case "forms.places-autocomplete-field":
                                        return (
                                          <PlacesAutocompleteField
                                            key={index}
                                            name={ic?.name}
                                            label={ic?.label}
                                            required={ic?.required}
                                            onPlaceChanged={onPlaceChanged}
                                            onLoad={onLoad}
                                          />
                                        );
                                        break;
                                      case "forms.files-field":
                                        return (
                                          <FilesField
                                            idField={ic?.idField}
                                            key={index}
                                            label={ic?.label}
                                            name={ic?.name}
                                            required={ic?.required}
                                            onChangeHandler={handleFileChange}
                                            description={ic?.description}
                                            explanation={ic?.explanation}
                                          />
                                        );
                                        break;
                                      case "forms.email-field":
                                        return (
                                          <EmailField
                                            idField={ic?.idField}
                                            key={index}
                                            label={ic?.label}
                                            name={ic?.name}
                                            value={inputsData[ic?.name] || ""}
                                            onChangeHandler={handleInputChange}
                                            placeholder={ic?.placeholder}
                                            readonly={ic?.readonly}
                                            required={ic?.required}
                                            isValid={
                                              inputsValidationData[ic?.name]
                                                ?.valid
                                            }
                                            errorMessage={
                                              inputsValidationData[ic?.name]
                                                ?.message
                                            }
                                            description={ic?.description}
                                            explanation={ic?.explanation}
                                          />
                                        );
                                        break;
                                      case "forms.signature":
                                        return (
                                          <SignatureField
                                            idField={ic?.idField}
                                            key={index}
                                            label={ic?.label}
                                            name={ic?.name}
                                            onChangeHandler={handleInputChange}
                                            required={ic?.required}
                                            value={inputsData[ic?.name] || ""}
                                            isValid={
                                              inputsValidationData[ic?.name]
                                                ?.valid
                                            }
                                            errorMessage={
                                              inputsValidationData[ic?.name]
                                                ?.message
                                            }
                                          />
                                        );
                                        break;
                                      default:
                                        break;
                                    }
                                  }
                                }
                              })}
                          </div>
                        </div>
                      ))}
                      <div className="tps-form-controls">
                        <a
                          className="button button-prev-step"
                          onClick={() => {
                            setActiveStep(activeStep - 1);
                          }}
                          tabIndex="-1"
                          style={{
                            display: activeStep > 0 ? "inline-flex" : "none",
                          }}
                        >
                          Previous Step
                        </a>
                        {activeStep === steps.length - 1 ? (
                          <input
                            className={`FormButton btn btn-primary button ${
                              activeStep < steps.length - 1 && "hide"
                            }`}
                            id={submitButtonId}
                            type="submit"
                            value={submitButtonValue || "Submit"}
                            disabled={loading}
                          />
                        ) : (
                          <a
                            className="button button-next-step"
                            onClick={() => {
                              if (validateFormStepData(activeStep)) {
                                setActiveStep(activeStep + 1);
                              }
                            }}
                            tabIndex="-1"
                          >
                            Next Step
                          </a>
                        )}
                      </div>
                      {formSent &&
                        formSubmittedAction !== "Redirect to URL" && (
                          <div className="fatv2-profile-form-feedback success">
                            {formSubmittedActionTextOrURL}
                          </div>
                        )}
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default Form;
